import { Dialog } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LearningTrailsService } from '@app/core';
import { notNull, selectedEntityActive } from '@app/shared/utils';
import { SIDENAV_START, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  exhaustMap,
  filter,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ModalSubdivisionsComponent } from '../../components/modal-subdivisions/modal-subdivisions.component';
import { SidenavLearningTrailsComponent } from '../../components/sidenav-learning-trails/sidenav-learning-trails.component';
import { Collection, Course } from '../../models';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-showcase',
  templateUrl: './showcase.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShowcaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public collectionIndexToOpened: number;
  public voucherEnabled: number;
  public courseSelected$: Observable<Course>;
  public collections$ = new BehaviorSubject<Collection[]>(undefined);
  public loading$ = new Subject<boolean>();

  protected readonly collectionsSelected = signal<number[]>([]);
  protected readonly coursesSelected = signal<number[]>([]);
  private readonly learningTrailsService = inject(LearningTrailsService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly sidenavData = inject<Observable<SidenavRef>>(SIDENAV_START);
  private readonly _activatedRoute = inject(ActivatedRoute);

  public ngOnInit() {
    this.sidenav.pipe(take(1)).subscribe((sidenav) => {
      sidenav.collections$.next([]);
      sidenav.search.setValue(this._activatedRoute.snapshot.queryParams.search);
    });

    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        notNull(),
        distinctUntilChanged(isEqual),
        tap(() => this.loading$.next(true)),
        switchMap((user) => this.learningTrailsService.showcase(selectedEntityActive(user)[0])),
        tap(({ data }) => this.collections$.next(data)),
        tap(({ data }) => {
          const courses = [];
          const collections = [];
          data.forEach((collection) => {
            collection.cursos.forEach((course) => {
              collections.push(collection.id);
              courses.push(course.id);
            });
          });
          this.collectionsSelected.set(collections);
          this.coursesSelected.set(courses);
        }),
        switchMap(({ data }) =>
          this.sidenav.pipe(
            take(1),
            tap((sidenav) => sidenav.collections$.next(data)),
          ),
        ),
        tap(() => this.loading$.next(false)),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.sidenav
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((sidenav) => sidenav.collectionsSelected$ as Observable<number[]>),
      )
      .subscribe((collectionsSelected) => this.collectionsSelected.set(collectionsSelected));

    this.sidenav
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((sidenav) => sidenav.coursesSelected$ as Observable<number[]>),
      )
      .subscribe((coursesSelected) => this.coursesSelected.set(coursesSelected));
  }

  public ngOnDestroy(): void {
    this.collections$.complete();
    this.loading$.complete();
  }

  public setCollectionIndexToOpened(value: number): void {
    this.collectionIndexToOpened = value;
  }

  public OnVoucherEnabled(value: number): void {
    this.voucherEnabled = value;

    const courses = this.collections$.value.map((collection) => ({
      ...collection,
      cursos: collection.cursos.map((course) => ({
        ...course,
        enable: course.produtos.some((product) => product.id === value),
      })),
    }));

    this.collections$.next(courses);
  }

  public setCourseSelected(course: Course): void {
    this.dialog
      .open(ModalSubdivisionsComponent, { data: { course, isShowcase: true } })
      .closed.pipe(notNull())
      .subscribe(() => ({}));
    this.courseSelected$ = of(course);
  }

  public filtered(id: number, selected: number[]): boolean {
    return selected.includes(id);
  }

  private get sidenav() {
    return this.sidenavData.pipe(
      notNull(),
      takeUntilDestroyed(this.destroyRef),
      exhaustMap((res) => res.componentInstance$),
      notNull(),
      distinctUntilChanged(isEqual),
      filter((res) => res instanceof SidenavLearningTrailsComponent),
    );
  }
}
